// @flow

import { AppListingCTA, HostingSelectorModal } from '@atlassian/mpac-app-listing';
import { AtlassianProductHostingTypeValues } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import React from 'react';

import { getMailToLink } from './HeaderInjection.utils';

import type {
  CTAKindType,
  HostingSelectorAppVersion,
  PurchaseIntentType,
} from '@atlassian/mpac-app-listing';
import type {
  AppDetailsLink,
  ArtifactDownloadLink,
  LicensePurchaseLink,
  ManagePrivateListingLink,
  TrialLicenseActivationLink,
} from '@atlassian/mpac-types';
import type { AtlassianProductHostingType } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';

export type AllVersions = {
  cloudAppVersion: ?HostingSelectorAppVersion,
  serverAppVersion: ?HostingSelectorAppVersion,
  dataCenterAppVersion: ?HostingSelectorAppVersion,
};

export type HeaderInjectionProps = {|
  appKey: string,
  appId: string,
  appName: string,
  productHostingOptions: $ReadOnlyArray<AtlassianProductHostingType>,
  selectedHosting: AtlassianProductHostingType,
  versionInfo: AllVersions,
  artifactDownloadLink: ArtifactDownloadLink,
  trialLicenseActivationLink: TrialLicenseActivationLink,
  licensePurchaseLink: LicensePurchaseLink,
  privateListingsLink: ManagePrivateListingLink,
  privateRemotableOnly: boolean,
  appDetailsLink: AppDetailsLink,
  hostingSelectorOpen: boolean,
  onHostingSelectorClose: () => void,
  activeCTAKind: CTAKindType,
  hostingSelectorPurchaseIntent: PurchaseIntentType,
  onRequestAppClick: () => void,
  onAppListingHeaderCTAClick: (
    e: SyntheticMouseEvent<HTMLElement>,
    productHostingOptions: $ReadOnlyArray<AtlassianProductHostingType>,
    purchaseIntent: PurchaseIntentType,
    ctaKind: CTAKindType,
    selectedVersion: ?HostingSelectorAppVersion
  ) => void,
  onHostingSelectorCTAClick: (
    e: SyntheticMouseEvent<HTMLElement>,
    hosting: AtlassianProductHostingType,
    appVersion: HostingSelectorAppVersion
  ) => void,
|};

export const getSelectedVersion = (
  selectedHosting: AtlassianProductHostingType,
  versionInfo: AllVersions
): ?HostingSelectorAppVersion => {
  switch (selectedHosting) {
    case AtlassianProductHostingTypeValues.Cloud:
      return versionInfo.cloudAppVersion;
    case AtlassianProductHostingTypeValues.Server:
      return versionInfo.serverAppVersion;
    case AtlassianProductHostingTypeValues.DataCenter:
      return versionInfo.dataCenterAppVersion;
    default:
      throw new Error('Unexpected hosting type: ' + selectedHosting);
  }
};

const HeaderInjection = (props: HeaderInjectionProps) => {
  const selectedVersion = getSelectedVersion(props.selectedHosting, props.versionInfo);
  const shareLink = getMailToLink(props.appName, props.appKey, props.appDetailsLink);

  const handleCTAClick = (e, purchaseIntent, ctaKind) => {
    props.onAppListingHeaderCTAClick(
      e,
      props.productHostingOptions,
      purchaseIntent,
      ctaKind,
      selectedVersion
    );
  };

  return (
    <>
      <HostingSelectorModal
        appKey={props.appKey}
        appId={props.appId}
        privateRemotableOnly={props.privateRemotableOnly}
        onCTAClick={props.onHostingSelectorCTAClick}
        artifactDownloadLink={props.artifactDownloadLink}
        trialLicenseActivationLink={props.trialLicenseActivationLink}
        licensePurchaseLink={props.licensePurchaseLink}
        privateListingsLink={props.privateListingsLink}
        purchaseIntent={props.hostingSelectorPurchaseIntent}
        activeCTAKind={props.activeCTAKind}
        highlightedHosting={props.selectedHosting}
        serverAppVersion={props.versionInfo.serverAppVersion}
        cloudAppVersion={props.versionInfo.cloudAppVersion}
        dataCenterAppVersion={props.versionInfo.dataCenterAppVersion}
        shareWithAdminLink={shareLink}
        isOpen={props.hostingSelectorOpen}
        onClose={props.onHostingSelectorClose}
        onRequestAppClick={props.onRequestAppClick}
      />

      {selectedVersion && (
        <AppListingCTA
          appKey={props.appKey}
          appId={props.appId}
          appVersion={selectedVersion}
          privateRemotableOnly={props.privateRemotableOnly}
          selectedHosting={props.selectedHosting}
          artifactDownloadLink={props.artifactDownloadLink}
          trialLicenseActivationLink={props.trialLicenseActivationLink}
          licensePurchaseLink={props.licensePurchaseLink}
          onClick={handleCTAClick}
          privateListingsLink={props.privateListingsLink}
        />
      )}
    </>
  );
};

export default HeaderInjection;
